import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/ui/Icon';

const ExtractedDataPanel = ({ contractData, onGenerateCalendar, onEditDate }) => {
  const navigate = useNavigate();
  const [representationRole, setRepresentationRole] = useState('buyer');

  // 🔥 DEBUG: Log all incoming data
  console.log('🔥 ExtractedDataPanel - ALL PROPS:', { contractData, onGenerateCalendar, onEditDate });
  console.log('🔥 ExtractedDataPanel - contractData structure:', JSON.stringify(contractData, null, 2));
  console.log('🔥 ExtractedDataPanel - extractedData from contractData:', contractData?.extractedData);
  console.log('🔥 ExtractedDataPanel - dates specifically:', contractData?.extractedData?.dates);
  console.log('🔥 ExtractedDataPanel - property specifically:', contractData?.extractedData?.property);
  console.log('🔥 ExtractedDataPanel - parties specifically:', contractData?.extractedData?.parties);
  console.log('🔥 ExtractedDataPanel - financial specifically:', contractData?.extractedData?.financial);
  console.log('🔥 ExtractedDataPanel - financial.deposits:', contractData?.extractedData?.financial?.deposits);
  console.log('🔥 ExtractedDataPanel - nonStandardClauses:', contractData?.extractedData?.nonStandardClauses);

  // Use real contract data or fallback to demo data
  const extractedData = contractData?.extractedData || {
    property: {
      address: "No contract data available",
      purchasePrice: "Upload and process a contract",
      mlsNumber: "N/A",
      propertyType: "N/A", 
      squareFootage: "N/A",
      lotSize: "N/A"
    },
    dates: {},
    escrow: {
      earnestMoney: "N/A",
      escrowCompany: "N/A",
      escrowOfficer: "N/A",
      phone: "N/A"
    },
    nonStandardClauses: []
  };


  // Get dates and implement new inspection grouping logic
  const dates = extractedData.dates || {};
  
  // Process dates with new inspection grouping logic
  const processedDates = {};
  const additionalTermsInspections = [];
  
  Object.entries(dates).forEach(([key, dateInfo]) => {
    if (!dateInfo || !dateInfo.value) return;
    
    // Handle main inspection deadline with grouped inspections
    if (key === 'inspectionDeadline') {
      const groupedInspections = dateInfo.groupedInspections || ['General Building'];
      processedDates[key] = {
        ...dateInfo,
        displayName: groupedInspections.length > 1 
          ? `Inspection Deadline (${groupedInspections.join(', ')})`
          : 'Inspection Deadline',
        groupedInspections
      };
    }
    // Handle separate inspection deadlines that go to Additional Terms
    else if (key.includes('InspectionDeadline') && dateInfo.value) {
      additionalTermsInspections.push({
        key,
        dateInfo,
        type: dateInfo.type || key.replace('InspectionDeadline', '').replace(/([A-Z])/g, ' $1').trim() + ' Inspection'
      });
    }
    // Handle all other dates normally
    else {
      processedDates[key] = dateInfo;
    }
  });
  
  const dateEntries = Object.entries(processedDates);
  
  // 🔥 DEBUG: Log dates structure
  console.log('🔥 DATES - Filtered dateEntries.length:', dateEntries.length);

  return (
    <div className="h-full overflow-auto p-4">
      {/* Grid-Style Summary Layout */}
      <div className="space-y-6">
        
        {/* Property Overview Card */}
        <div className="professional-card p-4">
          <div className="flex items-center mb-3">
            <Icon name="Home" size={20} color="var(--color-primary)" />
            <h3 className="text-lg font-semibold ml-2" style={{color: '#0d1519'}}>Property Overview</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-xs font-medium" style={{color: '#0d1519'}}>ADDRESS</p>
              <p className="text-sm font-medium" style={{color: '#0d1519'}}>{extractedData.property?.address || 'Not specified'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium" style={{color: '#0d1519'}}>PURCHASE PRICE</p>
              <p className="text-lg font-bold" style={{color: '#0d1519'}}>{extractedData.property?.purchasePrice || 'Not specified'}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium" style={{color: '#0d1519'}}>BUYER</p>
                <label className="flex items-center space-x-1 cursor-pointer">
                  <input
                    type="radio"
                    name="representationRole"
                    value="buyer"
                    checked={representationRole === 'buyer'}
                    onChange={(e) => setRepresentationRole(e.target.value)}
                    className="w-3 h-3 text-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="text-xs text-muted-foreground">I represent</span>
                </label>
              </div>
              <p className="text-sm font-medium" style={{color: '#0d1519'}}>{extractedData.parties?.buyer || 'Not specified'}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium" style={{color: '#0d1519'}}>SELLER</p>
                <label className="flex items-center space-x-1 cursor-pointer">
                  <input
                    type="radio"
                    name="representationRole"
                    value="seller"
                    checked={representationRole === 'seller'}
                    onChange={(e) => setRepresentationRole(e.target.value)}
                    className="w-3 h-3 text-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="text-xs text-muted-foreground">I represent</span>
                </label>
              </div>
              <p className="text-sm font-medium" style={{color: '#0d1519'}}>{extractedData.parties?.seller || 'Not specified'}</p>
            </div>
          </div>
        </div>

        {/* Key Dates Grid */}
        <div className="professional-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <Icon name="Calendar" size={20} color="var(--color-primary)" />
              <h3 className="text-lg font-semibold ml-2" style={{color: '#0d1519'}}>Important Dates</h3>
            </div>
            <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
              {dateEntries.length} dates found
            </span>
          </div>
          
          {dateEntries.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dateEntries.map(([key, dateInfo]) => {
                const dateValue = typeof dateInfo === 'object' && dateInfo?.value ? dateInfo.value : 
                                 typeof dateInfo === 'string' ? dateInfo : 'N/A';
                const confidence = typeof dateInfo === 'object' && dateInfo?.confidence ? dateInfo.confidence : null;
                
                return (
                  <div key={key} className="border border-border rounded-lg p-3 hover:bg-muted/20 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium" style={{color: '#0d1519'}}>
                        {dateInfo.displayName?.toUpperCase() || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).toUpperCase()}
                      </p>
                    </div>
                    <p className="text-sm font-bold mb-2" style={{color: '#0d1519'}}>{dateValue}</p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          // Create Google Calendar link directly
                          const dateValue = typeof dateInfo === 'object' && dateInfo?.value ? dateInfo.value : dateInfo;
                          const eventTitle = `${extractedData.property?.address || 'Property'} - ${dateInfo.displayName || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}`;
                          
                          try {
                            const eventDate = new Date(dateValue);
                            const startDate = eventDate.toISOString().split('T')[0].replace(/-/g, '');
                            
                            const params = new URLSearchParams({
                              action: 'TEMPLATE',
                              text: eventTitle,
                              dates: `${startDate}/${startDate}`,
                              details: `Contract deadline: ${dateInfo.displayName || key}`,
                              location: extractedData.property?.address || '',
                              ctz: Intl.DateTimeFormat().resolvedOptions().timeZone
                            });
                            
                            window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank', 'width=600,height=600');
                          } catch (error) {
                            console.error('Error creating calendar link:', error);
                            alert('Error creating calendar event');
                          }
                        }}
                        className="text-xs px-3 py-1 bg-black text-white rounded hover:opacity-90 transition-opacity font-medium focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Add to Calendar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Calendar" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
              <p>No dates extracted from contract</p>
            </div>
          )}
        </div>

        {/* Financial Details */}
        <div className="professional-card p-4">
          <div className="flex items-center mb-3">
            <Icon name="DollarSign" size={20} color="var(--color-primary)" />
            <h3 className="text-lg font-semibold ml-2" style={{color: '#0d1519'}}>Deposits</h3>
          </div>

          <div className="space-y-3">
            {(() => {
              // Handle both array (new format) and string (legacy format) deposits
              const deposits = extractedData.financial?.deposits;
              const depositsArray = Array.isArray(deposits) ? deposits : [deposits || extractedData.escrow?.earnestMoney || 'Not specified'];

              return (
                <div className="space-y-3">
                  {depositsArray.map((depositInfo, index) => {
                    if (!depositInfo || depositInfo === 'Not specified') return null;

                    // Extract date from deposit string if it exists
                    const dateMatch = depositInfo.match(/due\s+([\d\/\-]+)/i);
                    const hasDate = dateMatch && dateMatch[1];
                    const depositNumber = index + 1;
                    const depositLabel = depositsArray.length > 1
                      ? `${depositNumber}${depositNumber === 1 ? 'ST' : depositNumber === 2 ? 'ND' : 'RD'} DEPOSIT`
                      : 'DEPOSIT';

                    return (
                      <div key={index} className="border border-border rounded-lg p-3">
                        <div className="space-y-2">
                          <p className="text-xs font-medium" style={{color: '#0d1519'}}>{depositLabel}</p>
                          <p className="text-sm font-medium" style={{color: '#0d1519'}}>{depositInfo}</p>

                          {/* Show calendar button if deposit has a due date */}
                          {hasDate && (
                            <div className="flex">
                              <button
                                onClick={() => {
                                  const eventTitle = `${extractedData.property?.address || 'Property'} - ${depositLabel}`;
                                  const dateText = dateMatch[1];

                                  try {
                                    const eventDate = new Date(dateText);
                                    const startDate = eventDate.toISOString().split('T')[0].replace(/-/g, '');

                                    const params = new URLSearchParams({
                                      action: 'TEMPLATE',
                                      text: eventTitle,
                                      dates: `${startDate}/${startDate}`,
                                      details: 'Contract deposit payment due',
                                      location: extractedData.property?.address || '',
                                      ctz: Intl.DateTimeFormat().resolvedOptions().timeZone
                                    });

                                    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank', 'width=600,height=600');
                                  } catch (error) {
                                    console.error('Error creating calendar link:', error);
                                    alert('Error creating calendar event');
                                  }
                                }}
                                className="text-xs px-3 py-1 bg-black text-white rounded hover:opacity-90 transition-opacity font-medium focus:outline-none focus:ring-2 focus:ring-gray-500"
                              >
                                Add to Calendar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {depositsArray.every(d => !d || d === 'Not specified') && (
                    <p className="text-sm text-muted-foreground">No deposit information found</p>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Additional Terms */}
        {(extractedData.nonStandardClauses && extractedData.nonStandardClauses.length > 0) || additionalTermsInspections.length > 0 ? (
          <div className="professional-card p-4">
            <div className="flex items-center mb-3">
              <Icon name="AlertTriangle" size={20} color="var(--color-primary)" />
              <h3 className="text-lg font-semibold ml-2" style={{color: '#0d1519'}}>Additional Terms</h3>
              <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded ml-2">
                {(extractedData.nonStandardClauses?.length || 0) + additionalTermsInspections.length} items
              </span>
            </div>
            
            <div className="space-y-2">
              {/* Separate inspection deadlines */}
              {additionalTermsInspections.map((inspection, index) => (
                <div key={`inspection-${index}`} className="border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium" style={{color: '#0d1519'}}>
                      {inspection.type} Deadline: {inspection.dateInfo.value}
                    </p>
                  </div>
                  <div className="flex">
                    <button
                      onClick={() => {
                        // Create Google Calendar link directly for inspection
                        const eventTitle = `${extractedData.property?.address || 'Property'} - ${inspection.type}`;
                        
                        try {
                          const eventDate = new Date(inspection.dateInfo.value);
                          const startDate = eventDate.toISOString().split('T')[0].replace(/-/g, '');
                          
                          const params = new URLSearchParams({
                            action: 'TEMPLATE',
                            text: eventTitle,
                            dates: `${startDate}/${startDate}`,
                            details: `Contract inspection deadline: ${inspection.type}`,
                            location: extractedData.property?.address || '',
                            ctz: Intl.DateTimeFormat().resolvedOptions().timeZone
                          });
                          
                          window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank', 'width=600,height=600');
                        } catch (error) {
                          console.error('Error creating calendar link:', error);
                          alert('Error creating calendar event');
                        }
                      }}
                      className="text-xs px-3 py-1 bg-black text-white rounded hover:opacity-90 transition-opacity font-medium focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Add to Calendar
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Regular non-standard clauses */}
              {extractedData.nonStandardClauses?.map((clause, index) => (
                <div key={`clause-${index}`} className="border border-border rounded-lg p-3">
                  <p className="text-sm font-medium" style={{color: '#0d1519'}}>{clause}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4">
          {dateEntries.length > 0 && (
            <button
              onClick={() => {
                // Generate ICS file with all dates
                const allEvents = [];

                // Add all dates from processedDates (includes contract dates and deposit dates)
                dateEntries.forEach(([key, dateInfo]) => {
                  const dateValue = typeof dateInfo === 'object' && dateInfo?.value ? dateInfo.value : dateInfo;
                  const eventTitle = dateInfo.displayName || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                  if (dateValue && dateValue !== 'Not specified') {
                    const eventType = dateInfo.type === 'deposit' ? 'deposit payment' : 'deadline';
                    allEvents.push({
                      title: `${extractedData.property?.address || 'Property'} - ${eventTitle}`,
                      date: dateValue,
                      description: `Contract ${eventType}: ${eventTitle}`
                    });
                  }
                });

                // Add separate inspection deadlines
                additionalTermsInspections.forEach((inspection) => {
                  allEvents.push({
                    title: `${extractedData.property?.address || 'Property'} - ${inspection.type}`,
                    date: inspection.dateInfo.value,
                    description: `Contract inspection deadline: ${inspection.type}`
                  });
                });

                // Add deposit dates with calendar events
                const deposits = extractedData.financial?.deposits;
                const depositsArray = Array.isArray(deposits) ? deposits : [deposits].filter(Boolean);
                depositsArray.forEach((depositInfo, index) => {
                  if (depositInfo) {
                    const dateMatch = depositInfo.match(/due\s+([\d\/\-]+)/i);
                    if (dateMatch && dateMatch[1]) {
                      const depositNumber = index + 1;
                      const depositLabel = depositsArray.length > 1
                        ? `${depositNumber}${depositNumber === 1 ? 'ST' : depositNumber === 2 ? 'ND' : 'RD'} DEPOSIT`
                        : 'DEPOSIT';
                      allEvents.push({
                        title: `${extractedData.property?.address || 'Property'} - ${depositLabel}`,
                        date: dateMatch[1],
                        description: 'Contract deposit payment due'
                      });
                    }
                  }
                });

                // Generate ICS file
                const now = new Date();
                const timestamp = now.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

                const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Contract Parser//Contract Dates//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
${allEvents.map((event, index) => {
  try {
    const eventDate = new Date(event.date);
    const dateStr = eventDate.toISOString().split('T')[0].replace(/-/g, '');
    return `BEGIN:VEVENT
UID:${Date.now()}-${index}@contractparser.pro
SUMMARY:${event.title}
DTSTART;VALUE=DATE:${dateStr}
DTEND;VALUE=DATE:${dateStr}
DTSTAMP:${timestamp}
DESCRIPTION:${event.description}
LOCATION:${extractedData.property?.address || ''}
SEQUENCE:0
STATUS:CONFIRMED
END:VEVENT`;
  } catch (e) {
    return ''; // Skip invalid dates
  }
}).filter(Boolean).join('\n')}
END:VCALENDAR`;

                const blob = new Blob([icsContent], { type: 'text/calendar' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `contract-dates-${extractedData.property?.address?.replace(/[^a-zA-Z0-9]/g, '-') || 'property'}.ics`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="px-6 py-2 bg-black text-white rounded-lg hover:opacity-90 transition-opacity font-medium flex items-center focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <Icon name="Download" size={18} className="mr-2" />
              Download All as ICS
            </button>
          )}

          <button
            onClick={() => {
              // Try to access stored file data using the contract filename
              const filename = contractData?.contract_filename;
              const storedFileData = localStorage.getItem(`contract_file_${filename}`);

              if (storedFileData) {
                try {
                  // Create a download link from the base64 data
                  const link = document.createElement('a');
                  link.href = storedFileData;
                  link.download = filename || 'contract.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } catch (error) {
                  console.error('Download error:', error);
                  alert('Unable to download original document');
                }
              } else {
                alert('Original PDF file not found. Upload a new contract to enable PDF download.');
              }
            }}
            className="px-6 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors font-medium"
          >
            Download Contract
          </button>

          <button
            onClick={() => {
              // Store representation role for email generation
              localStorage.setItem('representationRole', representationRole);
              // Navigate to email template generator page using React Router
              navigate('/email-template-generator');
            }}
            className="px-6 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors font-medium"
          >
            Preview Client Email
          </button>
        </div>
        
      </div>
    </div>
  );
};

export default ExtractedDataPanel;